import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { CustomerService } from '../../../services/customer.service';
import { AuthService } from '../../../services/auth.service';
import { evaluatePasswordRules, PasswordRuleResults, newPasswordValidator } from '../../../validators/password.validator';

@Component({
  selector: 'app-customer-management-reset-password',
  templateUrl: './customer-management-reset-password.component.html',
  styleUrls: ['./customer-management-reset-password.component.scss']
})
export class CustomerManagementResetPasswordComponent implements OnInit {
  resetPasswordForm: UntypedFormGroup;
  customerId: any;
  customerName = '';
  loading = false;
  submitted = false;

  // True when this screen is shown as a mandatory step right after a
  // first-time customer login (driven by the `resetPassword` flag on the
  // customer-master/login response), rather than opened voluntarily from
  // "Edit Profile". In this mode the customer has no prior password to
  // enter, and the screen cannot be dismissed until reset is complete.
  isFirstLogin = false;

  hideOld = false;
  hideNew = false;
  hideConfirm = false;

  rules: PasswordRuleResults = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private customerService: CustomerService,
    private authService: AuthService,
    public location: Location
  ) {
    this.customerId = this.route.snapshot.params['id'];
    this.isFirstLogin = this.route.snapshot.queryParams['resetPassword'] === 'true';
  }

  ngOnInit(): void {
    this.resetPasswordForm = this.formBuilder.group(
      {
        oldPassword: [{ value: '', disabled: this.isFirstLogin }, this.isFirstLogin ? [] : [Validators.required]],
        newPassword: [
          '',
          [Validators.required, newPasswordValidator(() => this.passwordContext)]
        ],
        confirmPassword: ['', [Validators.required]]
      },
      { validators: this.passwordMatchValidator }
    );

    this.resetPasswordForm.get('newPassword').valueChanges.subscribe((value) => {
      this.rules = evaluatePasswordRules(value, this.passwordContext);
    });
    this.rules = evaluatePasswordRules('', this.passwordContext);

    this.loadCustomer();
  }

  get viewForm() {
    return this.resetPasswordForm;
  }

  get passwordContext() {
    const [firstName, ...rest] = (this.customerName || '').trim().split(/\s+/);
    const lastName = rest.join(' ');
    return {
      firstName: firstName || '',
      lastName: lastName || '',
      oldPassword: this.resetPasswordForm ? this.resetPasswordForm.get('oldPassword').value : ''
    };
  }

  loadCustomer() {
    this.loading = true;

    if (this.customerId) {
      // Admin/staff flow: id supplied via route param.
      this.customerService.getCustomerById(this.customerId).subscribe(
        (res: any) => {
          this.loading = false;
          if (res && res['result']) {
            this.customerName = res['result'].customerName || '';
          }
        },
        () => {
          this.loading = false;
        }
      );
    } else {
      // Self-service flow: logged-in customer resetting their own password.
      this.customerService.getCurrentMasterCustomer().subscribe(
        (res: any) => {
          this.loading = false;
          if (res) {
            this.customerId = res.id;
            this.customerName = res.customerName || '';
          }
        },
        () => {
          this.loading = false;
        }
      );
    }
  }

  passwordMatchValidator(frm: UntypedFormGroup) {
    const newPassword = frm.get('newPassword')?.value;
    const confirmPassword = frm.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { mismatch: true };
  }

  isRuleValid(rule: keyof PasswordRuleResults): boolean {
    return !!(this.rules && this.rules[rule]);
  }

  get allRulesValid(): boolean {
    if (!this.rules) {
      return false;
    }
    return Object.values(this.rules).every(Boolean);
  }

  onSubmit() {
    this.submitted = true;

    this.rules = evaluatePasswordRules(this.resetPasswordForm.get('newPassword').value, this.passwordContext);

    if (this.resetPasswordForm.invalid || !this.allRulesValid) {
      return;
    }

    this.loading = true;
    const payload = {
      customerId: this.customerId,
      oldPassword: this.resetPasswordForm.get('oldPassword').value,
      newPassword: this.resetPasswordForm.get('newPassword').value,
      firstLogin: this.isFirstLogin
    };

    this.customerService.resetCustomerPassword(payload).subscribe(
      (res: any) => {
        this.loading = false;
        if (res) {
          // Always log the customer out after a reset (first-login or
          // voluntary) so they must sign back in with the new password.
          alert('Password reset successfully. Please log in again with your new password.');
          this.authService.logout();
          sessionStorage.clear();
          this.router.navigate(['/eCert']);
        }
      },
      (error) => {
        this.loading = false;
        if (error instanceof Object && error['ResponseException']) {
          alert(error['ResponseException']);
        } else {
          alert('Failed to reset password.');
        }
      }
    );
  }
}
